import { Array as A, Order, pipe } from 'effect';

/**
 * A point of a two-way-match table cell as fractions of the page's width and height. The recognition engine sends
 * cells on the page with the file's own rotation applied and before any turn of the viewer.
 */
export type TwoWayMatchCellPoint = { X: number; Y: number };

export type TwoWayMatchCell = A.NonEmptyReadonlyArray<TwoWayMatchCellPoint>;

/** A cell's bounding box, as fractions of the page. */
export type TwoWayMatchCellBox = { left: number; top: number; right: number; bottom: number };

/** A step on the screen, y pointing down. */
export type ScreenStep = { dx: number; dy: number };

type Corners<T> = { topLeft: T; topRight: T; bottomRight: T; bottomLeft: T };

// 0–3 clockwise quarter turns; an angle that is not a whole number of quarters counts as no turn.
const quarterTurns = (rotation: number) => (Number.isInteger(rotation / 90) ? (((rotation / 90) % 4) + 4) % 4 : 0);

// Every turn in this layer is a whole number of quarters, so it is exact: no angle, no trigonometry.
const quarterTurnClockwise = ({ dx, dy }: ScreenStep): ScreenStep => ({ dx: -dy, dy: dx });

// The same quarter turn for a point of the page, around the page's centre.
const quarterTurnPointClockwise = ({ X, Y }: TwoWayMatchCellPoint): TwoWayMatchCellPoint => ({ X: 1 - Y, Y: X });

const repeatTurn = <T>(turn: (value: T) => T, value: T, times: number): T =>
  times === 0 ? value : repeatTurn(turn, turn(value), times - 1);

const turnStep = (step: ScreenStep, turns: number) => repeatTurn(quarterTurnClockwise, step, turns);

const turnPoint = (point: TwoWayMatchCellPoint, turns: number) => repeatTurn(quarterTurnPointClockwise, point, turns);

/** A cell as the page shows it: turned clockwise by the page's user rotation. */
export const displayedTwoWayMatchCell = (cell: TwoWayMatchCell, userRotation: number): TwoWayMatchCell =>
  A.map(cell, (point) => turnPoint(point, quarterTurns(userRotation)));

export const twoWayMatchCellBox = (cell: TwoWayMatchCell): TwoWayMatchCellBox => {
  const xs = A.map(cell, ({ X }) => X);
  const ys = A.map(cell, ({ Y }) => Y);
  return {
    left: A.min(xs, Order.number),
    top: A.min(ys, Order.number),
    right: A.max(xs, Order.number),
    bottom: A.max(ys, Order.number),
  };
};

/** Where the cells sit on a surface of the given size: their common bounding box, from page fractions to pixels. */
export const twoWayMatchCellsRect = (
  cells: A.NonEmptyReadonlyArray<TwoWayMatchCell>,
  { width, height }: { width: number; height: number },
) => {
  const { left, top, right, bottom } = twoWayMatchCellBox(A.flatten(cells));
  return { left: left * width, top: top * height, width: (right - left) * width, height: (bottom - top) * height };
};

/**
 * How far the page content is turned clockwise on the screen from standing upright: the recognized rotation is the
 * turn that makes the scan upright, the user rotation is the turn the page is shown at.
 */
export const turnFromUpright = (userRotation: number, recognizedRotation: number) =>
  quarterTurns(userRotation - recognizedRotation) * 90;

// In the upright frame a corner is the vertex furthest along its diagonal: top-left has the smallest X + Y, and so on.
const CORNER_REACH: Corners<(point: TwoWayMatchCellPoint) => number> = {
  topLeft: ({ X, Y }) => -(X + Y),
  topRight: ({ X, Y }) => X - Y,
  bottomRight: ({ X, Y }) => X + Y,
  bottomLeft: ({ X, Y }) => Y - X,
};

/**
 * Where on the screen each corner of a displayed cell lies, named as the corner it is when the content stands upright.
 * The corner is one of the cell's own vertices, so a slightly skewed cell keeps its exact corners.
 */
export const uprightTwoWayMatchCellCorners = (
  displayedCell: TwoWayMatchCell,
  turn: number,
): Corners<TwoWayMatchCellPoint> => {
  type Vertex = { point: TwoWayMatchCellPoint; upright: TwoWayMatchCellPoint };
  const vertices = A.map(
    displayedCell,
    (point): Vertex => ({ point, upright: turnPoint(point, (4 - quarterTurns(turn)) % 4) }),
  );
  const furthest = (reach: (point: TwoWayMatchCellPoint) => number) =>
    pipe(vertices, A.max(Order.mapInput(Order.number, ({ upright }: Vertex) => reach(upright)))).point;
  return {
    topLeft: furthest(CORNER_REACH.topLeft),
    topRight: furthest(CORNER_REACH.topRight),
    bottomRight: furthest(CORNER_REACH.bottomRight),
    bottomLeft: furthest(CORNER_REACH.bottomLeft),
  };
};

/** Where "up" and "right" of the upright page content point on the screen. */
export const uprightPageContentDirections = (turn: number): { up: ScreenStep; right: ScreenStep } => ({
  up: turnStep({ dx: 0, dy: -1 }, quarterTurns(turn)),
  right: turnStep({ dx: 1, dy: 0 }, quarterTurns(turn)),
});

/**
 * The column binding button in pixels of the displayed page: a square of `size` on the header cell's top-left corner
 * as the content stands upright, reaching up and right from it — outside the cell's top edge.
 */
export const twoWayMatchBindingButtonPosition = (
  displayedHeaderCell: TwoWayMatchCell,
  turn: number,
  page: { width: number; height: number },
  size: number,
) => {
  const { topLeft } = uprightTwoWayMatchCellCorners(displayedHeaderCell, turn);
  const { up, right } = uprightPageContentDirections(turn);
  return {
    top: page.height * topLeft.Y + size * (Math.min(0, up.dy) + Math.min(0, right.dy)),
    left: page.width * topLeft.X + size * (Math.min(0, up.dx) + Math.min(0, right.dx)),
  };
};

/**
 * The row marker's place: the middle of the row's end edge as the content stands upright — the right edge of the end
 * cell in a left-to-right interface, the left edge in a right-to-left one.
 */
export const twoWayMatchRowEndPoint = (displayedEndCell: TwoWayMatchCell, turn: number, rtl: boolean) => {
  const { topLeft, topRight, bottomRight, bottomLeft } = uprightTwoWayMatchCellCorners(displayedEndCell, turn);
  const [top, bottom] = rtl ? [topLeft, bottomLeft] : [topRight, bottomRight];
  return { X: (top.X + bottom.X) / 2, Y: (top.Y + bottom.Y) / 2 };
};
